// ============================================================================
// GsSegmented — contrôle segmenté mono-sélection (MASTER-GEST-E1A-ACCUEIL)
// ============================================================================
// Promotion du chore évoqué UI_STANDARDS §4c « éditeur de composition » : UNE
// primitive pour (a) le toggle de vues (variant 'default' — piste slate, actif =
// pastille blanche + ombre, NEUTRE : la sélection de segment n'est pas du
// wayfinding brand) et (b) la barre de filtres catégories (variant 'scrollable'
// — chips défilantes, actif = chip slate pleine, standard « chip filtre actif »
// UI_STANDARDS §4). Domaine-agnostique : libellés par props (JAMAIS de t() dans
// @gs/ui). Tokens slate-* + échelle nommée uniquement (règle #28) — zéro hex.
//
// A11y : role="radiogroup" + segments role="radio" aria-checked, roving
// tabindex, flèches ← → ↑ ↓ (sélection au déplacement, pattern radio natif).

import { useRef } from 'react';
import type { KeyboardEvent } from 'react';

export interface GsSegmentedOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface GsSegmentedProps {
  options: GsSegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  /** aria-label du groupe (i18n par prop — jamais de t() dans @gs/ui). */
  ariaLabel: string;
  /**
   * 'default'    — piste slate, actif = pastille blanche + ombre (toggle de vues).
   * 'scrollable' — chips défilantes sans piste (barre de filtres catégories) ;
   *                seul débordement horizontal légitime d'un écran (volontaire).
   */
  variant?: 'default' | 'scrollable';
  /** Segments étirés flex-1 (toggle pleine largeur mobile). Variant 'default' seulement. */
  fullWidth?: boolean;
  className?: string;
}

const TRACK_DEFAULT =
  'inline-flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800';
const TRACK_SCROLLABLE =
  'flex items-center gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

const SEGMENT_BASE =
  'touch-manipulation whitespace-nowrap rounded-md px-3.5 py-1.5 text-field transition-colors ' +
  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500/40 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const SEGMENT_ACTIVE =
  'bg-white font-semibold text-slate-800 shadow-xs dark:bg-slate-700 dark:text-slate-100';
const SEGMENT_INACTIVE =
  'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200';

const CHIP_BASE =
  'touch-manipulation whitespace-nowrap rounded-full px-3 py-1.5 text-field font-medium transition-colors ' +
  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500/40 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const CHIP_ACTIVE = 'bg-slate-800 font-semibold text-white dark:bg-slate-600';
const CHIP_INACTIVE =
  'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 ' +
  'dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200';

/**
 * Contrôle segmenté mono-sélection — toggle de vues ('default') ou barre de
 * filtres défilante ('scrollable'). L'actif est NEUTRE (pastille/chip slate,
 * jamais brand : la marque reste réservée au wayfinding).
 */
export function GsSegmented({
  options,
  value,
  onChange,
  ariaLabel,
  variant = 'default',
  fullWidth = false,
  className = '',
}: GsSegmentedProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const scrollable = variant === 'scrollable';

  // Roving tabindex : le segment actif porte tabIndex=0 (fallback 1er activable).
  const enabled = options.map((o, i) => ({ o, i })).filter(({ o }) => !o.disabled);
  const activeIndex = options.findIndex((o) => o.value === value && !o.disabled);
  const tabStop = activeIndex >= 0 ? activeIndex : enabled[0]?.i ?? -1;

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    const backward = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
    if (!forward && !backward) return;
    e.preventDefault();
    const pos = enabled.findIndex(({ i }) => i === index);
    if (pos < 0 || enabled.length === 0) return;
    const nextPos = (pos + (forward ? 1 : -1) + enabled.length) % enabled.length;
    const next = enabled[nextPos];
    refs.current[next.i]?.focus();
    onChange(next.o.value);
  };

  const track = scrollable ? TRACK_SCROLLABLE : `${TRACK_DEFAULT}${fullWidth ? ' flex w-full' : ''}`;

  return (
    <div role="radiogroup" aria-label={ariaLabel} className={`${track} ${className}`.trim()}>
      {options.map((option, index) => {
        const isActive = option.value === value;
        const cls = scrollable
          ? `${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`
          : `${SEGMENT_BASE} ${isActive ? SEGMENT_ACTIVE : SEGMENT_INACTIVE}${fullWidth ? ' flex-1 text-center' : ''}`;
        return (
          <button
            key={option.value}
            ref={(el) => { refs.current[index] = el; }}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={option.disabled}
            tabIndex={index === tabStop ? 0 : -1}
            onClick={() => { if (!isActive) onChange(option.value); }}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cls}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
